import { useEffect, useRef } from "react"
import {
  FiBold, FiItalic, FiUnderline, FiLink, FiList,
} from "react-icons/fi"
import {
  BsTypeStrikethrough, BsListOl, BsTypeH3,
} from "react-icons/bs"
import { MdFormatClear } from "react-icons/md"

const Sep = () => (
  <div className="mx-1 h-4 w-px flex-shrink-0 bg-richblack-600" />
)

const TBtn = ({ title, onMD, children }) => (
  <button
    type="button"
    title={title}
    onMouseDown={onMD}
    className="rounded p-1.5 text-richblack-300 transition-colors hover:bg-richblack-600 hover:text-richblack-5"
  >
    {children}
  </button>
)

export default function RichTextEditor({
  value = "",
  onChange,
  placeholder = "Enter description...",
  disabled = false,
  minHeight = 140,
}) {
  const editorRef = useRef(null)
  const lastUserHtml = useRef("")

  useEffect(() => {
    const el = editorRef.current
    if (!el) return
    // Only write to DOM when value is set externally (e.g., edit-mode load)
    if (value !== lastUserHtml.current) {
      el.innerHTML = value || ""
    }
  }, [value])

  const exec = (command, val = null) => {
    if (disabled) return
    document.execCommand(command, false, val)
    editorRef.current?.focus()
    emit()
  }

  const emit = () => {
    const html = editorRef.current?.innerHTML || ""
    lastUserHtml.current = html
    onChange?.(html)
  }

  const insertLink = () => {
    const url = window.prompt("Enter URL:", "https://")
    if (url) exec("createLink", url)
  }

  return (
    <div
      className={`overflow-hidden rounded-lg border bg-richblack-700 transition-colors ${
        disabled
          ? "border-richblack-600 opacity-60"
          : "border-richblack-500 focus-within:border-richblack-300"
      }`}
    >
      {!disabled && (
        <div className="flex flex-wrap items-center gap-0.5 border-b border-richblack-600 bg-richblack-800 px-2 py-1.5">
          <TBtn title="Bold (Ctrl+B)"       onMD={(e) => { e.preventDefault(); exec("bold") }}><FiBold size={14} /></TBtn>
          <TBtn title="Italic (Ctrl+I)"     onMD={(e) => { e.preventDefault(); exec("italic") }}><FiItalic size={14} /></TBtn>
          <TBtn title="Underline (Ctrl+U)"  onMD={(e) => { e.preventDefault(); exec("underline") }}><FiUnderline size={14} /></TBtn>
          <TBtn title="Strikethrough"       onMD={(e) => { e.preventDefault(); exec("strikeThrough") }}><BsTypeStrikethrough size={14} /></TBtn>
          <Sep />
          <TBtn title="Heading"             onMD={(e) => { e.preventDefault(); exec("formatBlock", "h3") }}><BsTypeH3 size={14} /></TBtn>
          <Sep />
          <TBtn title="Bullet List"         onMD={(e) => { e.preventDefault(); exec("insertUnorderedList") }}><FiList size={14} /></TBtn>
          <TBtn title="Numbered List"       onMD={(e) => { e.preventDefault(); exec("insertOrderedList") }}><BsListOl size={14} /></TBtn>
          <Sep />
          <TBtn title="Insert Link"         onMD={(e) => { e.preventDefault(); insertLink() }}><FiLink size={14} /></TBtn>
          <TBtn title="Clear Formatting"    onMD={(e) => { e.preventDefault(); exec("removeFormat") }}><MdFormatClear size={14} /></TBtn>
        </div>
      )}

      <div
        ref={editorRef}
        contentEditable={!disabled}
        suppressContentEditableWarning
        onInput={emit}
        data-placeholder={placeholder}
        style={{ minHeight }}
        className="rte-content max-w-none p-3 text-sm text-richblack-100 outline-none"
      />
    </div>
  )
}
